// FILE: src/pages/Profile.js
import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Grid,
    Avatar,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Badge,
    IconButton,
    Tab,
    Tabs,
    LinearProgress,
    InputAdornment,
    MenuItem,
    TextField,
    Divider,
    Snackbar,
} from '@mui/material'
import {
    Person,
    Email,
    PhoneAndroid,
    Home,
    LocationOn,
    Cake,
    Flag,
    VerifiedUser,
    GppGood,
    Warning,
    CloudUpload,
    Edit,
    Save,
    CheckCircle,
    AccessTime,
    Business,
    Category,
    WhatsApp,
    Public,
} from '@mui/icons-material'
import AnimatedSection from '../components/common/AnimatedSection'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'

// ✅ uses the same axios instance + token interceptor you already have
import api from '../services/api'

/* --------------------------
   Helpers
-------------------------- */

const splitName = (full = '') => {
    const s = String(full || '').trim()
    if (!s) return { firstName: '', lastName: '' }
    const parts = s.split(' ').filter(Boolean)
    if (parts.length === 1) return { firstName: parts[0], lastName: '' }
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

const DIAL_CODES = [
    { code: 'US', label: 'United States', dial: '+1' },
    { code: 'CA', label: 'Canada', dial: '+1' },
    { code: 'UK', label: 'United Kingdom', dial: '+44' },
    { code: 'AU', label: 'Australia', dial: '+61' },
    { code: 'FR', label: 'France', dial: '+33' },
    { code: 'SL', label: 'Sierra Leone', dial: '+232' },
    { code: 'LR', label: 'Liberia', dial: '+231' },
    { code: 'GN', label: 'Guinea', dial: '+224' },
]

const getDialFromCountry = (country) => {
    const found = DIAL_CODES.find((d) => d.code === country)
    return found?.dial || '+1'
}

const splitPhone = (raw = '') => {
    const s = String(raw || '').trim()
    if (!s) return { dialCode: '', phone: '' }

    const dialCandidates = [...new Set(DIAL_CODES.map((d) => d.dial))].sort((a, b) => b.length - a.length)
    const matched = dialCandidates.find((dial) => s.startsWith(dial))
    if (matched) {
        const rest = s.slice(matched.length).replace(/^\s+/, '')
        return { dialCode: matched, phone: rest.trim() }
    }

    const m = s.match(/^(\+\d{1,4})\s*(.*)$/)
    if (m) return { dialCode: m[1], phone: (m[2] || '').trim() }

    return { dialCode: '', phone: s }
}

const normalizePhoneWithDial = (dial, number) => {
    const d = String(dial || '').trim() || '+'
    const n = String(number || '').trim()
    const dialClean = d.startsWith('+') ? d : `+${d}`

    if (!n) return dialClean
    if (n.startsWith('+')) return n.replace(/\s+/g, ' ').trim()

    return `${dialClean} ${n.replace(/\s+/g, '')}`.trim()
}

const BUSINESS_CATEGORIES = [
    { id: 'schools', name: 'Schools' },
    { id: 'clinics', name: 'Clinics & pharmacies' },
    { id: 'home', name: 'Home services' },
    { id: 'vendors', name: 'Vendors' },
]

const BUSINESS_COUNTRIES = [
    { code: 'SL', label: 'Sierra Leone' },
    { code: 'LR', label: 'Liberia' },
    { code: 'GN', label: 'Guinea' },
]

const getAdminAreaLabel = (countryCode) => {
    if (countryCode === 'SL') return 'District'
    if (countryCode === 'LR') return 'County'
    if (countryCode === 'GN') return 'Prefecture'
    return 'District / County / Prefecture'
}

const isBusinessAccount = (u) => {
    const t = u?.accountType || u?.account_type
    return t === 'business' || t === 'nonprofit'
}

const getAuthToken = () => {
    return (
        localStorage.getItem('token') ||
        localStorage.getItem('auth_token') ||
        localStorage.getItem('access_token') ||
        ''
    )
}

const getApiBase = () => {
    const base = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || ''
    return String(base).replace(/\/$/, '')
}

const apiFetch = async (path, options = {}) => {
    const base = getApiBase()
    const token = getAuthToken()

    const normalizedPath = base.endsWith('/api') && path.startsWith('/api/')
        ? path.slice(4)
        : path
    const url = base ? `${base}${normalizedPath}` : path

    const headers = {
        Accept: 'application/json',
        ...(options.headers || {}),
    }

    if (options.body && !(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json'
    }

    if (token) headers.Authorization = `Bearer ${token}`

    const res = await fetch(url, { ...options, headers })

    const text = await res.text()
    let data = null
    try {
        data = text ? JSON.parse(text) : null
    } catch (e) {
        data = text || null
    }

    if (!res.ok) {
        const err = new Error('Request failed')
        err.status = res.status
        err.data = data
        throw err
    }

    return data
}

const patchSessionUser = (patch = {}) => {
    try {
        const raw = localStorage.getItem('session_user')
        if (!raw) return
        const obj = JSON.parse(raw)
        const next = { ...obj, ...patch }
        localStorage.setItem('session_user', JSON.stringify(next))
    } catch (e) {
        // ignore
    }
}

/* --------------------------
   Component
-------------------------- */

export default function Profile() {
    const { user, updateProfile } = useAuth()
    const { lang } = useLanguage()

    const businessMode = useMemo(() => isBusinessAccount(user), [user])

    const copy = useMemo(() => ({
        title: lang === 'fr' ? 'Mon profil' : 'My Profile',
        subtitle: lang === 'fr'
            ? 'Gerez vos informations et votre statut de verification'
            : 'Manage your information and verification status',
        personalInformation: lang === 'fr' ? 'Informations personnelles' : 'Personal Information',
        businessProfile: lang === 'fr' ? 'Profil entreprise' : 'Business Profile',
        verificationLevels: lang === 'fr' ? 'Niveaux de verification' : 'Verification Levels',
        securitySettings: lang === 'fr' ? 'Parametres de securite' : 'Security Settings',
        verificationStatus: lang === 'fr' ? 'Statut de verification' : 'Verification Status',
        completeVerification: lang === 'fr' ? 'Completer la verification' : 'Complete Verification',
        editProfile: lang === 'fr' ? 'Modifier le profil' : 'Edit Profile',
        cancel: lang === 'fr' ? 'Annuler' : 'Cancel',
        idCard: lang === 'fr' ? 'Piece d identite' : 'ID Card',
        proofOfAddress: lang === 'fr' ? 'Justificatif de domicile' : 'Proof of Address',
        selfie: lang === 'fr' ? 'Selfie' : 'Selfie',
        verified: lang === 'fr' ? 'Verifie' : 'Verified',
        pending: lang === 'fr' ? 'En attente' : 'Pending',
        notUploaded: lang === 'fr' ? 'Non telecharge' : 'Not_uploaded',
        upload: lang === 'fr' ? 'Telecharger' : 'Upload',
        twoFaEnabled: lang === 'fr' ? '2FA activee' : '2FA Enabled',
        firstName: lang === 'fr' ? 'Prenom' : 'First Name',
        lastName: lang === 'fr' ? 'Nom' : 'Last Name',
        email: lang === 'fr' ? 'Adresse e-mail' : 'Email Address',
        country: lang === 'fr' ? 'Pays' : 'Country',
        phoneNumber: lang === 'fr' ? 'Numero de telephone' : 'Phone Number',
        dateOfBirth: lang === 'fr' ? 'Date de naissance' : 'Date of Birth',
        address: lang === 'fr' ? 'Adresse' : 'Address',
        city: lang === 'fr' ? 'Ville' : 'City',
        postalCode: lang === 'fr' ? 'Code postal' : 'Postal Code',
        securityComingSoon: lang === 'fr'
            ? 'L interface des parametres de securite peut ensuite etre reliee aux vrais points d acces.'
            : 'Security settings UI can be wired to real endpoints next.',
    }), [lang])

    const [tabValue, setTabValue] = useState(0)
    const [editMode, setEditMode] = useState(false)
    const [kycDialog, setKycDialog] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [verificationDocument, setVerificationDocument] = useState(null)
    const [documentUploading, setDocumentUploading] = useState(false)
    const [documentError, setDocumentError] = useState('')

    // Toast
    const [toast, setToast] = useState({ open: false, type: 'info', message: '' })
    const showToast = (type, message) => setToast({ open: true, type, message })

    // Loading for personal save
    const [profileSaving, setProfileSaving] = useState(false)

    // ✅ Avatar upload
    const [avatarUploading, setAvatarUploading] = useState(false)
    const [avatarPreview, setAvatarPreview] = useState('')

    // Personal profile state
    const [profileData, setProfileData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        dialCode: '+1',
        phone: '',
        dateOfBirth: '',
        country: 'US',
        address: '',
        city: '',
        postalCode: '',
        occupation: '',
        company: '',
    })

    // Business profile state
    const [bizLoading, setBizLoading] = useState(false)
    const [bizEdit, setBizEdit] = useState(false)

    const [bizData, setBizData] = useState({
        businessName: '',
        category: '',
        businessCountry: 'SL',
        address: '',
        city: '',
        adminArea: '',
        whatsapp: '',
        emailPublic: '',
        speed: 'under_24_hours',
        status: 'pending',
    })

    const adminAreaLabel = useMemo(() => getAdminAreaLabel(bizData.businessCountry), [bizData.businessCountry])

    const tabs = useMemo(() => {
        const base = [{ label: copy.personalInformation }]
        if (businessMode) base.push({ label: copy.businessProfile })
        base.push({ label: copy.verificationLevels })
        base.push({ label: copy.securitySettings })
        return base
    }, [businessMode, copy])

    const tabIndex = useMemo(() => {
        return {
            personal: 0,
            business: businessMode ? 1 : -1,
            verification: businessMode ? 2 : 1,
            security: businessMode ? 3 : 2,
        }
    }, [businessMode])

    // Load user info into personal form
    useEffect(() => {
        if (!user) return

        const parts = splitName(user?.name || '')
        const firstName = user?.firstName || user?.first_name || parts.firstName || ''
        const lastName = user?.lastName || user?.last_name || parts.lastName || ''

        const rawPhone = user?.phone || user?.phone_number || ''
        const parsed = splitPhone(rawPhone)

        const country = user?.country || 'US'
        const dial = parsed.dialCode || getDialFromCountry(country)

        const dob = user?.dateOfBirth || user?.dob || user?.date_of_birth || ''

        setProfileData((prev) => ({
            ...prev,
            firstName,
            lastName,
            email: user?.email || '',
            country,
            dialCode: dial,
            phone: parsed.phone || '',
            dateOfBirth: dob ? String(dob).slice(0, 10) : '',
            address: user?.address || '',
            city: user?.city || '',
            postalCode: user?.postalCode || user?.postal_code || user?.zip || '',
            occupation: user?.occupation || '',
            company: user?.company || '',
        }))

        // prefill avatar if exists
        const existingAvatar = user?.avatar_url || user?.avatarUrl || ''
        if (existingAvatar && !avatarPreview) setAvatarPreview(existingAvatar)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user])

    // Load business profile
    useEffect(() => {
        if (!businessMode) return

        const base = getApiBase()
        if (!base) {
            showToast(
                'warning',
                'Set REACT_APP_API_URL (or REACT_APP_API_BASE_URL) in your React .env, then restart npm start.'
            )
            return
        }

        const load = async () => {
            setBizLoading(true)
            try {
                const data = await apiFetch('/api/business-profile', { method: 'GET' })
                const p = data?.profile || data?.data?.profile || data?.data || data?.profile || {}

                const country = p?.country || p?.businessCountry || user?.country || 'SL'
                const dial = getDialFromCountry(country)

                const rawWhatsapp = p?.whatsapp || ''
                const whatsappValue =
                    rawWhatsapp && rawWhatsapp.startsWith('+')
                        ? rawWhatsapp
                        : rawWhatsapp
                            ? normalizePhoneWithDial(dial, rawWhatsapp)
                            : dial

                setBizData((prev) => ({
                    ...prev,
                    businessName: p?.business_name || p?.businessName || user?.business_name || user?.name || '',
                    category: String(p?.category?.slug || p?.category || p?.category_id || ''),
                    businessCountry: country,
                    address: p?.address || '',
                    city: p?.city || '',
                    adminArea: p?.admin_area || p?.adminArea || '',
                    whatsapp: whatsappValue,
                    emailPublic: p?.email_public || p?.emailPublic || '',
                    speed: p?.speed || 'under_24_hours',
                    status: p?.status || 'pending',
                }))
            } catch (e) {
                if (e?.status === 404) {
                    showToast('warning', 'Missing endpoint. Add GET/PUT /api/business-profile in Laravel.')
                } else if (e?.status === 401) {
                    showToast('error', 'Not logged in. Please log in again.')
                } else {
                    showToast('error', 'Could not load business profile.')
                }
            } finally {
                setBizLoading(false)
            }
        }

        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessMode])

    // KYC (UI only)
    const kycStatus = {
        level: 2,
        status: 'pending',
        documents: [
            { type: copy.idCard, status: 'verified', uploaded: true },
            { type: copy.proofOfAddress, status: 'pending', uploaded: true },
            { type: copy.selfie, status: 'not_uploaded', uploaded: false },
        ],
    }

    const verificationLevels = [
        { level: 1, name: 'Basic', requirements: ['Email Verification', 'Phone Verification'], limit: '$1,000/month' },
        { level: 2, name: 'Enhanced', requirements: ['Government ID', 'Proof of Address'], limit: '$10,000/month' },
        { level: 3, name: 'Premium', requirements: ['Face Verification', 'Source of Funds'], limit: 'Unlimited' },
    ]

    const handleInputChange = (field) => (event) => {
        setProfileData((prev) => ({ ...prev, [field]: event.target.value }))
    }

    // ✅ REAL SAVE (personal)
    const handleSaveProfile = async () => {
        try {
            setProfileSaving(true)

            const payload = {
                firstName: String(profileData.firstName || '').trim(),
                lastName: String(profileData.lastName || '').trim(),
                country: profileData.country,
                phone: normalizePhoneWithDial(profileData.dialCode, profileData.phone),
                dateOfBirth: profileData.dateOfBirth || null,
                address: String(profileData.address || '').trim(),
                city: String(profileData.city || '').trim(),
                postalCode: String(profileData.postalCode || '').trim(),
            }

            await updateProfile(payload)

            showToast('success', 'Profile saved.')
            setEditMode(false)
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || 'Could not save profile.'
            showToast('error', msg)
        } finally {
            setProfileSaving(false)
        }
    }

    // ✅ Avatar upload (POST multipart) using same axios instance
    // IMPORTANT FIX: authService axios instance defaults Content-Type: application/json.
    // We must override it here so Laravel receives the file (otherwise: "avatar field is required").
    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return

        // allow selecting same file again
        e.target.value = ''

        const okTypes = ['image/jpeg', 'image/png', 'image/webp']
        if (!okTypes.includes(file.type)) return showToast('error', 'Use JPG, PNG, or WEBP.')
        if (file.size > 5 * 1024 * 1024) return showToast('error', 'Image too large (max 5MB).')

        const localUrl = URL.createObjectURL(file)
        setAvatarPreview(localUrl)

        try {
            setAvatarUploading(true)

            const form = new FormData()
            form.append('avatar', file)

            const res = await api.post('/auth/avatar', form, {
                headers: {
                    // ✅ this is the key fix
                    'Content-Type': 'multipart/form-data',
                },
            })

            const data = res?.data || {}
            const newUrl =
                data?.avatar_url ||
                data?.avatarUrl ||
                data?.user?.avatar_url ||
                data?.user?.avatarUrl ||
                ''

            if (newUrl) {
                setAvatarPreview(newUrl)
                patchSessionUser({ avatar_url: newUrl, avatarUrl: newUrl })
            }

            showToast('success', 'Profile photo updated.')
        } catch (err) {
            const existingAvatar = user?.avatar_url || user?.avatarUrl || ''
            setAvatarPreview(existingAvatar || '')
            showToast('error', err?.response?.data?.message || 'Could not upload photo.')
        } finally {
            setAvatarUploading(false)
        }
    }

    const handleBizChange = (field) => (event) => {
        const value = event.target.value

        if (field === 'businessCountry') {
            const dial = getDialFromCountry(value)

            setBizData((prev) => {
                const current = String(prev.whatsapp || '').trim()
                if (!current) return { ...prev, businessCountry: value, whatsapp: dial }
                if (!current.startsWith('+')) return { ...prev, businessCountry: value, whatsapp: normalizePhoneWithDial(dial, current) }
                return { ...prev, businessCountry: value }
            })

            return
        }

        setBizData((prev) => ({ ...prev, [field]: value }))
    }

    // business save (unchanged)
    const handleSaveBusiness = async () => {
        const base = getApiBase()
        if (!base) {
            showToast(
                'warning',
                'Set REACT_APP_API_URL (or REACT_APP_API_BASE_URL) in your React .env, then restart npm start.'
            )
            return
        }

        if (!bizData.businessName.trim()) return showToast('error', 'Business name is required.')
        if (!bizData.category) return showToast('error', 'Category is required.')
        if (!bizData.businessCountry) return showToast('error', 'Business country is required.')

        setBizLoading(true)
        try {
            const payload = {
                business_name: bizData.businessName.trim(),
                category: bizData.category,
                country: bizData.businessCountry,
                address: bizData.address.trim(),
                city: bizData.city.trim(),
                admin_area: bizData.adminArea.trim(),
                whatsapp: String(bizData.whatsapp || '').trim(),
                email_public: bizData.emailPublic.trim(),
                speed: bizData.speed,
            }

            await apiFetch('/api/business-profile', {
                method: 'PUT',
                body: JSON.stringify(payload),
            })

            showToast('success', 'Business profile saved.')
            setBizEdit(false)
        } catch (e) {
            if (e?.status === 404) {
                showToast('warning', 'Missing endpoint. Add GET/PUT /api/business-profile in Laravel.')
            } else if (e?.status === 422) {
                showToast('error', e?.data?.message || 'Please check the fields and try again.')
            } else if (e?.status === 401) {
                showToast('error', 'Not logged in. Please log in again.')
            } else {
                showToast('error', e?.data?.message || 'Could not save business profile.')
            }
        } finally {
            setBizLoading(false)
        }
    }

    const copyPhoneToWhatsapp = () => {
        const dial = profileData.dialCode || getDialFromCountry(profileData.country)
        const full = normalizePhoneWithDial(dial, profileData.phone)
        setBizData((p) => ({ ...p, whatsapp: full }))
    }

    const handleFileUpload = (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        event.target.value = ''
        const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
        if (!allowed.includes(file.type)) {
            setDocumentError('Use a JPG, PNG, WEBP, or PDF document.')
            return
        }
        if (file.size > 10 * 1024 * 1024) {
            setDocumentError('Document is too large. Maximum size is 10 MB.')
            return
        }
        setVerificationDocument(file)
        setUploadProgress(0)
        setDocumentError('')
    }

    const submitVerificationDocument = async () => {
        if (!verificationDocument || documentUploading) return
        const form = new FormData()
        form.append('document', verificationDocument)
        form.append('document_type', businessMode ? 'business_document' : 'proof_of_address')

        try {
            setDocumentUploading(true)
            setDocumentError('')
            setUploadProgress(1)
            const response = await api.post('/auth/verification-documents', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 60000,
                onUploadProgress: (event) => {
                    if (!event.total) return
                    setUploadProgress(Math.min(95, Math.round((event.loaded * 100) / event.total)))
                },
            })
            setUploadProgress(100)
            showToast('success', response?.data?.message || 'Document submitted for review.')
            setVerificationDocument(null)
            setKycDialog(false)
        } catch (error) {
            setUploadProgress(0)
            const message = error?.code === 'ECONNABORTED'
                ? 'The upload timed out. Check your connection and try again.'
                : error?.response?.data?.message || 'Document upload failed. Please try again.'
            setDocumentError(message)
            showToast('error', message)
        } finally {
            setDocumentUploading(false)
        }
    }

    const closeKycDialog = () => {
        if (documentUploading) return
        setKycDialog(false)
        setVerificationDocument(null)
        setUploadProgress(0)
        setDocumentError('')
    }

    const getStatusIcon = (status) => {
        if (status === 'verified') return <CheckCircle sx={{ color: 'success.main' }} />
        if (status === 'pending') return <AccessTime sx={{ color: 'warning.main' }} />
        return <Warning sx={{ color: 'text.secondary' }} />
    }

    const getStatusColor = (status) => {
        if (status === 'verified') return 'success'
        if (status === 'pending') return 'warning'
        if (status === 'rejected') return 'error'
        return 'default'
    }

    const getStatusLabel = (status) => {
        if (status === 'verified') return copy.verified
        if (status === 'pending') return copy.pending
        if (status === 'not_uploaded') return copy.notUploaded
        if (status === 'rejected') return lang === 'fr' ? 'Rejete' : 'Rejected'
        return status
    }

    const initials = `${(profileData.firstName || 'U')[0] || 'U'}${(profileData.lastName || '')[0] || ''}`.trim()

    const avatarSrc = avatarPreview || user?.avatar_url || user?.avatarUrl || ''

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <AnimatedSection>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" gutterBottom>
                            {copy.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {copy.subtitle}
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={4}>
                            <Card sx={{ mb: 3 }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        badgeContent={
                                            <>
                                                <input
                                                    accept="image/jpeg,image/png,image/webp"
                                                    style={{ display: 'none' }}
                                                    id="avatar-upload"
                                                    type="file"
                                                    onChange={handleAvatarUpload}
                                                />
                                                <label htmlFor="avatar-upload">
                                                    <IconButton
                                                        component="span"
                                                        size="small"
                                                        disabled={avatarUploading}
                                                        sx={{
                                                            bgcolor: 'primary.main',
                                                            color: 'white',
                                                            '&:hover': { bgcolor: 'primary.dark' },
                                                        }}
                                                    >
                                                        <CloudUpload fontSize="small" />
                                                    </IconButton>
                                                </label>
                                            </>
                                        }
                                    >
                                        <Avatar
                                            src={avatarSrc}
                                            sx={{ width: 120, height: 120, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}
                                        >
                                            {initials || 'U'}
                                        </Avatar>
                                    </Badge>

                                    <Typography variant="h5" gutterBottom>
                                        {(profileData.firstName || 'User').trim()} {(profileData.lastName || '').trim()}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {profileData.email || '—'}
                                    </Typography>

                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        <Chip
                                            icon={<VerifiedUser />}
                                            label={lang === 'fr' ? `Niveau ${kycStatus.level} verifie` : `Level ${kycStatus.level} Verified`}
                                            color={getStatusColor(kycStatus.status)}
                                            size="small"
                                        />
                                        <Chip icon={<GppGood />} label={copy.twoFaEnabled} color="success" size="small" />
                                        {businessMode && (
                                            <Chip
                                                icon={<Business />}
                                                label={lang === 'fr'
                                                    ? `Entreprise : ${getStatusLabel(bizData.status || 'pending')}`
                                                    : `Business: ${getStatusLabel(bizData.status || 'pending')}`}
                                                color={getStatusColor(bizData.status)}
                                                size="small"
                                            />
                                        )}
                                    </Box>

                                    <Button
                                        variant="outlined"
                                        startIcon={<Edit />}
                                        onClick={() => setEditMode((v) => !v)}
                                        size="small"
                                    >
                                        {editMode ? copy.cancel : copy.editProfile}
                                    </Button>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {copy.verificationStatus}
                                    </Typography>
                                    <List>
                                        {kycStatus.documents.map((doc, index) => (
                                            <ListItem key={index} sx={{ px: 0 }}>
                                                <ListItemIcon>{getStatusIcon(doc.status)}</ListItemIcon>
                                                <ListItemText
                                                    primary={doc.type}
                                                    secondary={getStatusLabel(doc.status)}
                                                />
                                                {!doc.uploaded && (
                                                        <Button size="small" onClick={() => setKycDialog(true)}>
                                                            {copy.upload}
                                                        </Button>
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Button fullWidth variant="contained" onClick={() => setKycDialog(true)} sx={{ mt: 2 }}>
                                        {copy.completeVerification}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} lg={8}>
                            <Card>
                                <CardContent>
                                    <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
                                        {tabs.map((t, i) => (
                                            <Tab key={`${t.label}-${i}`} label={t.label} />
                                        ))}
                                    </Tabs>

                                    {/* Personal Information */}
                                    {tabValue === tabIndex.personal && (
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.firstName}
                                                    value={profileData.firstName}
                                                    onChange={handleInputChange('firstName')}
                                                    disabled={!editMode}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Person />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.lastName}
                                                    value={profileData.lastName}
                                                    onChange={handleInputChange('lastName')}
                                                    disabled={!editMode}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Person />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.email}
                                                    type="email"
                                                    value={profileData.email}
                                                    disabled
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Email />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.country}
                                                    select
                                                    value={profileData.country}
                                                    onChange={(e) => {
                                                        const newCountry = e.target.value
                                                        setProfileData((prev) => ({
                                                            ...prev,
                                                            country: newCountry,
                                                            dialCode: getDialFromCountry(newCountry),
                                                        }))
                                                    }}
                                                    disabled={!editMode}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Flag />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                >
                                                    {DIAL_CODES.map((c) => (
                                                        <MenuItem key={c.code} value={c.code}>
                                                            {c.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.phoneNumber}
                                                    value={profileData.phone}
                                                    onChange={(e) => setProfileData((prev) => ({ ...prev, phone: e.target.value }))}
                                                    disabled={!editMode}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <TextField
                                                                    select
                                                                    value={profileData.dialCode}
                                                                    onChange={(e) =>
                                                                        setProfileData((prev) => ({ ...prev, dialCode: e.target.value }))
                                                                    }
                                                                    disabled={!editMode}
                                                                    variant="standard"
                                                                    sx={{ width: 90 }}
                                                                >
                                                                    {DIAL_CODES.map((c) => (
                                                                        <MenuItem key={`${c.code}-${c.dial}`} value={c.dial}>
                                                                            {c.dial}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <PhoneAndroid />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.dateOfBirth}
                                                    type="date"
                                                    value={profileData.dateOfBirth}
                                                    onChange={handleInputChange('dateOfBirth')}
                                                    disabled={!editMode}
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Cake />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.address}
                                                    value={profileData.address}
                                                    onChange={handleInputChange('address')}
                                                    disabled={!editMode}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Home />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.city}
                                                    value={profileData.city}
                                                    onChange={handleInputChange('city')}
                                                    disabled={!editMode}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <LocationOn />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={copy.postalCode}
                                                    value={profileData.postalCode}
                                                    onChange={handleInputChange('postalCode')}
                                                    disabled={!editMode}
                                                />
                                            </Grid>

                                            {editMode && (
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<Save />}
                                                        onClick={handleSaveProfile}
                                                        disabled={profileSaving}
                                                    >
                                                        {profileSaving ? 'Saving...' : 'Save Changes'}
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}

                                    {/* Business Profile (unchanged) */}
                                    {businessMode && tabValue === tabIndex.business && (
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 2,
                                                    gap: 2,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="h6">Business Profile</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        This is what customers see when they search for your business.
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<Edit />}
                                                        onClick={() => setBizEdit((v) => !v)}
                                                        disabled={bizLoading}
                                                    >
                                                        {bizEdit ? 'Cancel' : 'Edit Business'}
                                                    </Button>

                                                    {bizEdit && (
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<Save />}
                                                            onClick={handleSaveBusiness}
                                                            disabled={bizLoading}
                                                        >
                                                            Save
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>

                                            {bizLoading && <LinearProgress sx={{ mb: 2 }} />}

                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Business name"
                                                        value={bizData.businessName}
                                                        onChange={handleBizChange('businessName')}
                                                        disabled={!bizEdit}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Business />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        select
                                                        label="Category"
                                                        value={bizData.category}
                                                        onChange={handleBizChange('category')}
                                                        disabled={!bizEdit}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Category />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    >
                                                        <MenuItem value="" disabled>
                                                            Select a category
                                                        </MenuItem>
                                                        {BUSINESS_CATEGORIES.map((c) => (
                                                            <MenuItem key={c.id} value={c.id}>
                                                                {c.name}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        select
                                                        label="Business country"
                                                        value={bizData.businessCountry}
                                                        onChange={handleBizChange('businessCountry')}
                                                        disabled={!bizEdit}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Public />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    >
                                                        {BUSINESS_COUNTRIES.map((c) => (
                                                            <MenuItem key={c.code} value={c.code}>
                                                                {c.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Address"
                                                        value={bizData.address}
                                                        onChange={handleBizChange('address')}
                                                        disabled={!bizEdit}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Home />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="City"
                                                        value={bizData.city}
                                                        onChange={handleBizChange('city')}
                                                        disabled={!bizEdit}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <LocationOn />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label={adminAreaLabel}
                                                        value={bizData.adminArea}
                                                        onChange={handleBizChange('adminArea')}
                                                        disabled={!bizEdit}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <LocationOn />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="WhatsApp"
                                                        value={bizData.whatsapp}
                                                        onChange={handleBizChange('whatsapp')}
                                                        disabled={!bizEdit}
                                                        placeholder={getDialFromCountry(bizData.businessCountry)}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <WhatsApp />
                                                                </InputAdornment>
                                                            ),
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <Button
                                                                        size="small"
                                                                        type="button"
                                                                        onClick={copyPhoneToWhatsapp}
                                                                        disabled={!bizEdit || !(profileData.phone || '').trim()}
                                                                        sx={{ minWidth: 0 }}
                                                                    >
                                                                        Use my phone
                                                                    </Button>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Public email (optional)"
                                                        value={bizData.emailPublic}
                                                        onChange={handleBizChange('emailPublic')}
                                                        disabled={!bizEdit}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Email />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Divider sx={{ my: 1 }} />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        select
                                                        label="Delivery speed"
                                                        value={bizData.speed}
                                                        onChange={handleBizChange('speed')}
                                                        disabled={!bizEdit}
                                                    >
                                                        <MenuItem value="same_day">Same day</MenuItem>
                                                        <MenuItem value="under_24_hours">Under 24 hours</MenuItem>
                                                    </TextField>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField fullWidth label="Status" value={bizData.status} disabled />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Verification Levels */}
                                    {tabValue === tabIndex.verification && (
                                        <Box>
                                            {verificationLevels.map((level, index) => (
                                                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                                                    <CardContent>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                mb: 2,
                                                            }}
                                                        >
                                                            <Typography variant="h6">
                                                                Level {level.level}: {level.name}
                                                            </Typography>
                                                            <Chip
                                                                label={
                                                                    kycStatus.level > index
                                                                        ? copy.verified
                                                                        : kycStatus.level === index
                                                                            ? (lang === 'fr' ? 'Actuel' : 'Current')
                                                                            : (lang === 'fr' ? 'Verrouille' : 'Locked')
                                                                }
                                                                color={
                                                                    kycStatus.level > index
                                                                        ? 'success'
                                                                        : kycStatus.level === index
                                                                            ? 'primary'
                                                                            : 'default'
                                                                }
                                                                size="small"
                                                            />
                                                        </Box>

                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                            Requirements:
                                                        </Typography>

                                                        <List dense>
                                                            {level.requirements.map((req, reqIndex) => (
                                                                <ListItem key={reqIndex} sx={{ py: 0 }}>
                                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                                        <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                                                                    </ListItemIcon>
                                                                    <ListItemText primary={req} />
                                                                </ListItem>
                                                            ))}
                                                        </List>

                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Monthly Limit: <strong>{level.limit}</strong>
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Security Settings */}
                                    {tabValue === tabIndex.security && (
                                        <Typography variant="body2" color="text.secondary">
                                            {copy.securityComingSoon}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </AnimatedSection>

                <Dialog open={kycDialog} onClose={closeKycDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{businessMode ? 'Upload Company Document' : 'Upload Verification Document'}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {businessMode
                                    ? 'Upload a clear company registration or business verification document.'
                                    : 'Upload a clear proof-of-address document.'}
                            </Typography>

                            <Box sx={{ mt: 3 }}>
                                <input
                                    accept="image/*,.pdf"
                                    style={{ display: 'none' }}
                                    id="document-upload"
                                    type="file"
                                    onChange={handleFileUpload}
                                />
                                <label htmlFor="document-upload">
                                    <Button variant="outlined" component="span" startIcon={<CloudUpload />} fullWidth disabled={documentUploading}>
                                        Choose Document
                                    </Button>
                                </label>

                                {verificationDocument && (
                                    <Typography variant="body2" sx={{ mt: 1.5, overflowWrap: 'anywhere' }}>
                                        Selected: <strong>{verificationDocument.name}</strong>
                                    </Typography>
                                )}

                                {documentError && <Alert severity="error" sx={{ mt: 2 }}>{documentError}</Alert>}

                                {uploadProgress > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" gutterBottom>
                                            Upload Progress
                                        </Typography>
                                        <LinearProgress variant="determinate" value={uploadProgress} />
                                        <Typography variant="caption" color="text.secondary">
                                            {uploadProgress >= 95 && documentUploading
                                                ? 'Upload received. Processing securely...'
                                                : `${uploadProgress}%`}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeKycDialog} disabled={documentUploading}>Cancel</Button>
                        <Button variant="contained" disabled={!verificationDocument || documentUploading} onClick={submitVerificationDocument}>
                            {documentUploading ? 'Uploading...' : 'Submit Document'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={toast.open}
                    autoHideDuration={5000}
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setToast((t) => ({ ...t, open: false }))}
                        severity={toast.type}
                        sx={{ width: '100%' }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    )
}
